import { Alert, AlertColor, Snackbar } from "@mui/material";
import { createContext, FC, ReactNode, useReducer } from "react";
import PropTypes from "prop-types";

interface SnackbarContextState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

type SnackbarContextValue = {
  showSnackbar: (message: string, severity: AlertColor) => void;
};

const SnackbarContext = createContext<SnackbarContextValue>({
  showSnackbar: () => {},
});

enum ActionTypes {
  showSnackbar,
  closeSnackbar,
}

type ShowSnackbarAction = {
  type: ActionTypes.showSnackbar;
  payload: {
    message: string;
    severity: AlertColor;
  };
};

type CloseSnackbarAction = {
  type: ActionTypes.closeSnackbar;
};

type SnackbarContextAction = ShowSnackbarAction | CloseSnackbarAction;

const initialSnackbarState: SnackbarContextState = {
  message: "",
  open: false,
  severity: "info",
};

const reducer = (
  state: SnackbarContextState,
  action: SnackbarContextAction
): SnackbarContextState => {
  switch (action.type) {
    case ActionTypes.showSnackbar:
      const { message, severity } = action.payload;
      return {
        open: true,
        message,
        severity,
      };
    case ActionTypes.closeSnackbar:
      return {
        ...state,
        open: false,
      };

    default:
      return { ...state };
  }
};

interface SnackbarContextProviderProps {
  children: ReactNode;
}

const SnackbarProvider: FC<SnackbarContextProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialSnackbarState);

  const showSnackbar = (message: string, severity: AlertColor): void => {
    dispatch({
      type: ActionTypes.showSnackbar,
      payload: { message, severity },
    });
  };

  const handleClose = (): void => {
    dispatch({ type: ActionTypes.closeSnackbar });
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity={state.severity}>
          {state.message}
        </Alert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { SnackbarContext, SnackbarProvider };
